import React, { useEffect } from "react";
import "./About.css";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".ab-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-wrapper">
      <Helmet>
  <title>dmyor - For People Who Are Done Just Thinking About It</title>
  <link rel="canonical" href="https://www.dmyor.com/about" />
  <meta name="description" content="dmyor is a long-term planning tool where you write where you want to be, track your progress, and release your story when it's done." />
  <meta property="og:title" content="dmyor - For People Who Are Done Just Thinking About It" />
  <meta property="og:description" content="dmyor is a long-term planning tool where you write where you want to be, track your progress, and release your story when it's done." />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://dmyor.com/about" />
  <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="dmyor - For People Who Are Done Just Thinking About It" />
  <meta name="twitter:description" content="dmyor is a long-term planning tool where you write where you want to be, track your progress, and release your story when it's done." />
  <meta name="twitter:image" content="https://dmyor.com/images/og-image.png" />
</Helmet>


      {/* ── HERO ── */}
      <section className="ab-hero">
        <div className="ab-cosmos" aria-hidden="true">
          <div className="ab-star ab-star-1" />
          <div className="ab-star ab-star-2" />
          <div className="ab-star ab-star-3" />
          <div className="ab-star ab-star-4" />
          <div className="ab-star ab-star-5" />
          <div className="ab-star ab-star-6" />
          <div className="ab-node-dot" />
          <div className="ab-light-beam" />
        </div>

        <div className="ab-hero-content">
          <div className="ab-eyebrow ab-reveal">What is dmyor</div>
          <h1 className="ab-hero-title ab-reveal">
            Out of countless possibilities<br />
            of your future<br />
            <span className="ab-accent">make the one you want to exist a reality.</span>
          </h1>
          {/* <p className="ab-hero-sub ab-reveal">
            A Record is a reality you write before it exists, one of the many possible future you could live. You are the captain of ship and you decide which reality you want to tap into. Dmyor is to help you plan, track, and document the journey to that reality. Not just the destination, but the full arc of how you get there.
          </p> */}
        </div>
      </section>


      {/* ── WHY WE BUILT THIS ── */}
      <section className="ab-manifesto">
        <div className="ab-manifesto-inner">

          <h2 className="ab-manifesto-title ab-reveal">
            We built dmyor because<br />
            <span className="ab-accent">history forgets the people who tried.</span>
          </h2>

          <div className="ab-manifesto-body">
            <p className="ab-reveal">
              We know the names of those who made it. We have their books,
              their companies, their monuments. What we don't have is the record
              of what they were thinking before any of it existed. The 2am
              certainty, the doubt in month nine, the moment they almost stopped.
              That part disappears. It always has.
            </p>

            <p className="ab-reveal">
              dmyor exists to change that. Not as a productivity tool. As an
              archive. A place where the intention is written before the outcome
              is known, where the effort is documented as it happens, where the
              full arc of a human being trying to build something real is preserved.
              Not just the result.
            </p>

            <p className="ab-reveal">
              Think about the version of you five years from now. They will look
              back at this period of your life and see one of two things: evidence
              that you believed in something and pursued it, or silence. dmyor
              is the place that makes that evidence possible. Not to perform for
              an audience. To create a record for yourself, and eventually, for
              whoever comes after you.
            </p>

            <div className="ab-manifesto-pull ab-reveal">
              "What would your future self think, looking back at what you wrote
              about this time? Did you make it? Did you even try?"
            </div>

            <p className="ab-reveal">
              The world is moving faster than any previous generation has
              experienced. The decisions being made right now, by people in
              their twenties and thirties who are building companies, raising
              families, changing careers, choosing cities, are literally shaping
              what comes next. Future generations will want to know who these
              people were. Not just what they built. What they were reaching for.
            </p>

            <p className="ab-reveal">
              A vision is not a wish. It is a declaration made before the outcome
              is known, documented through all the hard and good parts of making
              it real, and eventually released with your name on it, when the
              written reality and the actual reality are finally the same thing.
            </p>

            <p className="ab-reveal">
              And it is not only for those who make it. The person who wrote
              a vision and fell short, who tried for two years and had to stop,
              who gave everything they had and still came up against something
              bigger than them. That story is worth keeping too. Effort is not
              erased by outcome. The attempt is real. The journey is real. And
              it deserves to exist somewhere, permanently, as proof that it
              happened.
            </p>

            <p className="ab-reveal">
              At the core of all of this is a simple belief: that writing
              something down with full intention, and then going out and making
              it real, is one of the most powerful things a person can do. Writing it as if it is already decided,
              and then living in a way that proves it. That is the mindset
              dmyor is built for. That is what this place exists to support.
            </p>

            <p className="ab-reveal">
              That record is worth keeping. dmyor keeps it.
            </p>
          </div>

        </div>
      </section>

    
      {/* ── WHY ── */}
      <section className="ab-section ab-why-section">
        <div className="ab-why-inner">
          <blockquote className="ab-quote ab-reveal">
            "Most people will die with the best version of their life still
            only existing in their head. dmyor exists so that doesn't have to
            be true."
          </blockquote>
          {/* <p className="ab-why-body ab-reveal">
            We built dmyor because there was no honest place to put a big idea
            before it exists. Somewhere private enough to be real, structured
            enough to be a commitment, and permanent enough to matter.
          </p>
          <p className="ab-why-body ab-reveal">
            The writing is the declaration. The updates are the proof of work.
            The release is the reality standing. dmyor is the record of all three.
          </p> */}

          <div className="ab-cta ab-reveal">
            <Link to="/login" className="ab-btn-primary">
              Put your first Record down
            </Link>
            <Link to="/explore" className="ab-btn-ghost">
              See what people are building
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;